import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Stack } from '@mui/material'
import _ from 'lodash'

import { SpatialPolicy as Policy } from '@/models/spatialPolicy'

import { EditPolicy } from '../../policyEditController'
import { FormErrors, PolicyFormFieldName } from '../../utils/types'
import { EditableFieldProps } from '../PolicyDetails/Form/fields/types'
import { getFields } from '../PolicyDetails/Form/policyTypeFieldValidations'

import { DateRangeField, DaysField, TimeFrameField } from './fields/scheduleFields'
import {
  AllocationField,
  OperatorsField,
  PolicyTypeField,
  ShapeLayerField,
  UnitField,
  VehicleCountMethodField,
  VehicleStatesField,
  VehicleTypesField,
} from './fields/selectFields'
import {
  DescriptionField,
  MaximumField,
  MessagesField,
  MinimumField,
  MinMaxGroup,
  ValueField,
} from './fields/textFields'
import { VariablePricesField } from './fields/ThemedTimetable'

interface ThemedPolicyFormProps {
  policy: Policy
  // only supplied by the editable (edit/create/duplicate) view; the read-only details view omits it
  editPolicy?: EditPolicy
  editable?: boolean
  errors?: FormErrors
  clearErrors?: () => void
  setErrors?: (errors: FormErrors) => void
}

/**
 * The new Mobility theme look of the policy form, read-only on the details page and editable on
 * the edit, create and duplicate pages. It shows the same fields and checks them in the same way
 * as the old `Form`. The policy status is not a field here: `PolicyStatusChip` shows it next to
 * the policy name.
 */
export const ThemedPolicyForm: FC<ThemedPolicyFormProps> = ({
  policy,
  editPolicy,
  editable = false,
  errors,
  clearErrors,
  setErrors,
}) => {
  const { t } = useTranslation()

  // The fields of the policy type.
  const fields = getFields(policy.policyType)

  const onValidate = (name: PolicyFormFieldName) => {
    const validator = fields[name]?.validator
    const error = validator && validator(policy)
    setErrors?.({ ...errors, [name]: error })
  }

  const fieldProps = (field: PolicyFormFieldName): EditableFieldProps => {
    const errorText = errors?.[field]
    return {
      editable,
      editPolicy,
      error: !!errorText,
      errorText,
      onValidate: () => onValidate(field),
      policy,
      required: fields[field]?.required,
    }
  }

  return (
    <Stack component="form" spacing={2.5} noValidate onSubmit={event => event.preventDefault()}>
      {!_.values(errors).every(_.isEmpty) && (
        <Alert severity="error">
          {t('policiesLibrary.formResolveErrors', 'Please resolve errors to save.')}
        </Alert>
      )}
      {fields.policyType && (
        <PolicyTypeField {...fieldProps('policyType')} clearErrors={clearErrors} />
      )}
      {fields.description && <DescriptionField {...fieldProps('description')} />}
      {fields.dateRange && <DateRangeField {...fieldProps('dateRange')} />}
      {fields.days && <DaysField {...fieldProps('days')} />}
      {fields.timeFrame && <TimeFrameField {...fieldProps('timeFrame')} />}
      {fields.shapeLayerUUID && <ShapeLayerField {...fieldProps('shapeLayerUUID')} />}
      {fields.value && <ValueField {...fieldProps('value')} />}
      {fields.variablePrices && <VariablePricesField {...fieldProps('variablePrices')} />}
      {fields.operators && <OperatorsField {...fieldProps('operators')} />}
      {fields.vehicleTypes && <VehicleTypesField {...fieldProps('vehicleTypes')} />}
      {fields.vehicleStates && <VehicleStatesField {...fieldProps('vehicleStates')} />}
      {fields.allocation && <AllocationField {...fieldProps('allocation')} />}
      {fields.vehicleCountMethod && (
        <VehicleCountMethodField {...fieldProps('vehicleCountMethod')} />
      )}
      {(fields.minimum || fields.maximum || fields.unit) && (
        <MinMaxGroup policy={policy}>
          {fields.minimum && <MinimumField {...fieldProps('minimum')} />}
          {fields.maximum && <MaximumField {...fieldProps('maximum')} />}
          {fields.unit && <UnitField {...fieldProps('unit')} />}
        </MinMaxGroup>
      )}
      {fields.messages && <MessagesField {...fieldProps('messages')} />}
    </Stack>
  )
}
